import React from 'react';
import PageTemplate from '../components/PageTemplate';
import '../styles/about-team.css';

// Import team member images to ensure they're included in the build
import joshuaHartImg from '../images/team/joshua-hart.jpg';
import aliYousefiImg from '../images/team/ali-yousefi.jpg';
import matthewGoffImg from '../images/team/matthew-goff.jpg';
import hartPosenImg from '../images/team/hart-posen.jpg';
import anishMoreImg from '../images/team/anish-more.jpg';
import laurenLuImg from '../images/team/lauren-lu.jpg';
import jimKeanImg from '../images/team/jim-kean.jpg';

const TeamPage = () => {
  const founders = [
    {
      name: 'Joshua Hart, MBA, DBA',
      title: 'Co-founder, CEO',
      bio: 'A doctoral candidate in Business Administration specializing in Executive Coaching at Liberty University graduating in June 2025, with nearly two decades of experience in operations and strategic leadership. Currently serving as a Captain of United Airlines and Aviation Consultant at JobSeekers, Joshua has previously held leadership positions at the Air Line Pilots Association where he drove a 24% engagement increase through strategic communications.',
      image: joshuaHartImg,
      education: 'DBA from Liberty University, MBA from Embry-Riddle Aeronautical University',
      experience: [
        '- Captain at United Airlines',
        '- Aviation Consultant at JobSeekers',
        '- Operations Director for the US Army Reserve',
        '- Leadership positions at the Air Line Pilots Association'
      ],
      skills: [
        '- Strategic leadership',
        '- Operations management',
        '- Data-driven decision making',
        '- Team building and development',
        '- Process optimization'
      ]
    },
    {
      name: 'Ali Yousefi, MBA, MPH',
      title: 'Co-founder, CAIO, CTO',
      bio: 'An MBA/MPH dual-degree candidate at Dartmouth (Tuck School of Business and Geisel School of Medicine) graduating in June 2025. Ali has a strong background combining healthcare, technology, and business expertise. Prior to founding Patients Empowered, he worked as an AI Scientist and Strategy & Commercialization Associate at Molecular You, as well as positions in quality assurance at Cardinal Health and as a Research Scientist at Apotex.',
      image: aliYousefiImg,
      education: 'Bachelor of Science in Biology and Chemistry, University of Manitoba',
      experience: [
        '- AI Scientist and Strategy & Commercialization Associate at Molecular You',
        '- Quality Assurance at Cardinal Health',
        '- Research Scientist at Apotex',
        '- Healthcare Strategy Analyst for Dartmouth-Hitchcock'
      ],
      skills: [
        '- AI applications for healthcare',
        '- Predictive analytics',
        '- Local LLM deployment',
        '- Business strategy development',
        '- Process optimization (Lean Six Sigma)'
      ]
    },
    {
      name: 'Matthew Goff, MD, MBA',
      title: 'Founding Chief Medical Officer',
      bio: 'An MD and 2025 MBA candidate at Dartmouth\'s Tuck School with specialized experience in healthcare innovation. His background in neuroimmune psychiatry and experience with the Levy Incubator inform his evidence-based approach to healthcare. Following graduation, Dr. Goff will begin residency in the Triple Board program at Stanford Children\'s Health, bringing critical pediatric mental health expertise.',
      image: matthewGoffImg,
      education: 'MD, MBA candidate at Dartmouth\'s Tuck School of Business',
      experience: [
        '- Healthcare innovation specialist',
        '- Research in neuroimmune psychiatry',
        '- Experience with the Levy Incubator for Healthcare Delivery',
        '- Triple Board program residency at Stanford Children\'s Health'
      ],
      skills: [
        '- Clinical insight and expertise',
        '- Strategic healthcare communications',
        '- Evidence-based medicine',
        '- Human-centered healthcare design',
        '- Pediatric and adolescent mental health'
      ]
    }
  ];

  const advisoryBoard = [
    {
      name: 'Prof. Hart Posen, PhD',
      title: 'Advisor',
      bio: 'Faculty Director of the Center for Entrepreneurship at Dartmouth\'s Tuck School, specializing in strategy and organizational learning. His expertise in how collective intelligence emerges in organizations directly informs our product development. He holds a PhD and MSc from Wharton, and an MBA from Ivey Business School.',
      image: hartPosenImg
    },
    {
      name: 'Anish More, MBA',
      title: 'Advisor',
      bio: 'Healthcare VC at RA Capital Management with experience evaluating healthcare investments. Previously Sr. Director at Signify Health and Director of Product Strategy for AI/ML Products at Aetna/CVS Health, where he launched patient engagement solutions generating $35MM+ in revenue. Anish holds an MBA from the Tuck School of Business at Dartmouth and a BA in Supply Chain Management from Michigan State University.',
      image: anishMoreImg
    },
    {
      name: 'Prof. Lauren Lu, PhD',
      title: 'Advisor',
      bio: 'Professor at Dartmouth\'s Tuck School with expertise in healthcare management and operations. Her data-driven research and editorial roles in leading management science journals provide strategic guidance for our healthcare operations. Lauren holds a PhD from Northwestern University, an MS from Stanford University, and an MA from Johns Hopkins University.',
      image: laurenLuImg
    },
    {
      name: 'Jim Kean, MBA',
      title: 'Advisor',
      bio: 'CEO at \'Molecular You\' with extensive experience in predictive healthcare technology. His background in AI-powered health analytics and previous experience developing platforms connecting diagnostic manufacturers, providers, and consumers brings invaluable industry perspective. James holds an MBA (T\'91) from the Tuck School of Business at Dartmouth.',
      image: jimKeanImg
    }
  ];

  return (
    <PageTemplate
      title="Our Team"
      heroDescription="Meet the visionaries behind Patients Empowered"
    >
      <div className="team-container">
      <section className="about-section">
        <h2 className="section-title">Founders</h2>
        <p className="text-center mb-5">
          Patients Empowered was founded with a mission to transform healthcare through AI that empowers both patients and providers.
        </p>

        {founders.map((founder, founderIndex) => (
          <div key={founderIndex} className="founder-profile card mb-5">
            <div className="grid grid-cols-1 grid-md-cols-3 gap-lg">
              <div className="text-center">
                <div
                  className="founder-image"
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-background-alt)',
                    backgroundImage: `url(${founder.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    margin: '0 auto 20px',
                    border: '4px solid var(--color-primary-light)',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                />
                <h3 className="mb-1">{founder.name}</h3>
                <p className="text-muted mb-3">{founder.title}</p>
                <p className="mb-3"><strong>Education:</strong><br />{founder.education}</p>
              </div>

              <div className="grid-md-cols-2">
                <div className="mb-4">
                  <h4 className="mb-3">About</h4>
                  <p>{founder.bio}</p>
                </div>

                <div>
                  <h4 className="mb-3">Experience</h4>
                  <ul className="mb-0">
                    {founder.experience.map((exp, index) => (
                      <li key={index} className="mb-2">{exp}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="mb-3">Skills & Expertise</h4>
                <ul className="mb-0">
                  {founder.skills.map((skill, index) => (
                    <li key={index} className="mb-2">{skill}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="about-section">
        <h2 className="section-title">Our Advisory Board</h2>
        <p className="text-center mb-5">
          Our advisory board consists of specialists who ensure that Patients Empowered delivers accurate, ethical, and patient-centered healthcare solutions.
        </p>

        <div className="grid grid-cols-1 grid-md-cols-2 gap-lg">
          {advisoryBoard.map((member, index) => (
            <div key={index} className="advisor-card card">
              <div className="flex gap-md">
                <div className="text-center" style={{ minWidth: '120px' }}>
                  <div
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-background-alt)',
                      backgroundImage: `url(${member.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      margin: '0 auto 15px',
                      border: '3px solid var(--color-primary-light)',
                      boxShadow: 'var(--shadow-md)'
                    }}
                  />
                </div>
                <div>
                  <h3 className="mb-1">{member.name}</h3>
                  <p className="advisor-title mb-2">{member.title}</p>
                  <p>{member.bio}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="about-section join-team-section">
        <h2 className="section-title">Join Our Mission</h2>
        <div className="card text-center">
          <p className="mb-4">
            We're looking for passionate individuals who share our vision of transforming healthcare through AI that empowers both patients and providers.
          </p>
          <div className="text-center">
            <a href="/careers" className="btn btn-primary">View Open Positions</a>
          </div>
        </div>
      </section>
      </div>
    </PageTemplate>
  );
};

export default TeamPage;
